import apiClient from "@/api/client";
import type {
  AccessGroupLite,
  DriveNode,
  DriveQuota,
  DriveShare,
  DriveUser,
  GisPreview,
  NodeListMeta,
  ShareAccess,
  UploadPlan,
} from "./types";

const BASE = "/two-drive";

const unwrap = <T>(p: Promise<{ data: { data: T } }>): Promise<T> => p.then((r) => r.data.data);

export interface InitiatePayload {
  name: string;
  parent: string | null;
  size_bytes: number;
  content_type: string;
}

export interface ShareCreatePayload {
  access: ShareAccess;
  shared_with_user?: string;
  shared_with_group?: string;
}

export interface DownloadResult {
  url?: string | null;
  filename?: string;
  task_id?: string;
}

export const twoDriveApi = {
  listNodes: (parent: string | null): Promise<{ nodes: DriveNode[]; meta: NodeListMeta }> =>
    apiClient
      .get(`${BASE}/nodes/`, { params: { parent: parent ?? "root" } })
      .then((r) => ({ nodes: r.data.data as DriveNode[], meta: r.data.meta as NodeListMeta })),

  getNode: (id: string): Promise<DriveNode> => unwrap(apiClient.get(`${BASE}/nodes/${id}/`)),

  createFolder: (name: string, parent: string | null): Promise<DriveNode> =>
    unwrap(apiClient.post(`${BASE}/folders/`, { name, parent })),

  // Get-or-create a chain of nested folders under `parent`; returns the deepest one.
  ensureFolderPath: (parent: string | null, path: string[]): Promise<DriveNode> =>
    unwrap(apiClient.post(`${BASE}/folders/ensure-path/`, { parent, path })),

  initiateUpload: (payload: InitiatePayload): Promise<UploadPlan> =>
    unwrap(apiClient.post(`${BASE}/files/initiate/`, payload)),

  localUpload: (nodeId: string, file: File, onProgress?: (pct: number) => void) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post(`${BASE}/files/${nodeId}/local-upload/`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total ?? file.size)))
        : undefined,
    });
  },

  completeUpload: (
    nodeId: string,
    body: { upload_id?: string; parts?: { part_number: number; etag: string }[] },
  ): Promise<DriveNode> => unwrap(apiClient.post(`${BASE}/files/${nodeId}/complete/`, body)),

  abortUpload: (nodeId: string, upload_id?: string) =>
    apiClient.post(`${BASE}/files/${nodeId}/abort/`, { upload_id }),

  renameMove: (id: string, body: { name?: string; parent?: string | null }): Promise<DriveNode> =>
    unwrap(apiClient.patch(`${BASE}/nodes/${id}/`, body)),

  // Duplicate nodes into `parent` (null = my root). Files reference the same
  // stored object as the original; the copy is owned by the destination owner.
  copyNodes: (ids: string[], parent: string | null): Promise<DriveNode[]> =>
    unwrap(apiClient.post(`${BASE}/nodes/copy/`, { ids, parent })),

  // Pack a mixed selection of files/folders into one background zip job.
  zipNodes: (ids: string[]): Promise<{ task_id: string }> =>
    unwrap(apiClient.post(`${BASE}/nodes/zip/`, { ids })),

  remove: (id: string) => apiClient.delete(`${BASE}/nodes/${id}/`),

  download: (id: string): Promise<DownloadResult> => unwrap(apiClient.get(`${BASE}/nodes/${id}/download/`)),
  gisPreview: (id: string): Promise<GisPreview> =>
    unwrap(apiClient.get(`${BASE}/nodes/${id}/gis-preview/`)),

  listShares: (id: string): Promise<DriveShare[]> => unwrap(apiClient.get(`${BASE}/nodes/${id}/shares/`)),

  createShare: (id: string, body: ShareCreatePayload): Promise<DriveShare> =>
    unwrap(apiClient.post(`${BASE}/nodes/${id}/shares/`, body)),

  deleteShare: (id: string) => apiClient.delete(`${BASE}/shares/${id}/`),

  sharedWithMe: (): Promise<DriveShare[]> => unwrap(apiClient.get(`${BASE}/shared-with-me/`)),

  sharedByMe: (): Promise<DriveShare[]> => unwrap(apiClient.get(`${BASE}/shared-by-me/`)),

  // Kto może być na dysku (udostępnianie + panel limitów CEO): pracownicy GK
  // i podwykonawcy wpuszczeni przez CEO. Osobna lista od pickera Friday, bo ten
  // jest zamknięty dla podwykonawców i nie zna flagi dysku.
  listUsers: (): Promise<DriveUser[]> => unwrap(apiClient.get(`${BASE}/users/`)),

  quota: (): Promise<DriveQuota> => unwrap(apiClient.get(`${BASE}/quota/`)),

  setQuota: (userId: string, quota_bytes: number): Promise<DriveQuota> =>
    unwrap(apiClient.patch(`${BASE}/quota/${userId}/`, { quota_bytes })),

  // Sharing groups reuse the existing owner-private access groups from the repository app.
  listGroups: (): Promise<AccessGroupLite[]> => unwrap(apiClient.get(`/repository/access-groups/`)),

  createGroup: (name: string, member_ids: string[]): Promise<AccessGroupLite> =>
    unwrap(apiClient.post(`/repository/access-groups/`, { name, member_ids })),
};
